
import Button from '@mui/material/Button'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import EmojiPicker from 'components/EmojiPicker'
import FeedsList from 'components/FeedsList'
import AIChatPanel from 'components/AIChatPanel'
import TagPopup from 'components/TagPopup'
import useAsyncAction from 'hooks/useAsyncAction'
import { useMountEffect } from 'hooks/useMountEffect'
import { Feed } from 'interfaces'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch } from 'react-redux'
import { Observable, of as observableOf, Subject } from 'rxjs'
import { debounceTime, filter, map, tap } from 'rxjs/operators'
import { checkFeatureAvailability } from 'services/product'
import { BRAND_BLUESKY, FEATURE_POST_TAGGING } from 'shared/constants'
import { COMPOSER_STATUS_KEY_GENERIC, PostDestinationType } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import { getTextFromHtml } from 'utils/composer'
import { sanitizeComposerHtml, sanitizeImageUrl, sanitizeUrl } from 'utils/sanitize'
import styles from './TextEditor.pcss'
import { BRAND_FACEBOOK, BRAND_LINKEDIN, BRAND_TWITTER } from 'shared'

const NODE_NAME_TEXT = '#text'
const MIN_TAG_SEARCH_LENGTH = 2
const MIN_LI_TAG_SEARCH_LENGTH = 3

const CHANGE_DEBOUNCE_TIME = 250
const TAG_DEBOUNCE_TIME = 50

export type CaptionPlaceholder = 'title' | 'description' | 'link' | 'caption' | 'credit'
export interface TextEditorProps {
  /**
   * Optional HTML string to override the current input value
   *
   * @type {string}
   * @memberof TextEditorProps
   */
  id?: string
  html?: string
  initialHTML: string
  hidden?: boolean
  disabled?: boolean
  network?: PostDestinationType | typeof COMPOSER_STATUS_KEY_GENERIC
  withEmojis?: boolean
  withGenerateText?: boolean
  inputClassName?: string
  footerElements?: React.ReactNode
  footerClassName?: string
  placeholder?: string
  disabledClassName?: string
  containerClassName?: string
  templateStrings?: CaptionPlaceholder[]
  tabIndex?: number
  onChange: (text: string, html: string) => void
  /**
   * Called before any user input mutates the editor (typing, paste, emoji).
   * Return false to block the input. Useful for feature-gating the editor.
   */
  onBeforeInput?: () => boolean
  searchTags?: (needle: string) => Observable<Feed[]>
}

export function TextEditor(props: TextEditorProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  let searchTagsFn = props.searchTags
  if (!searchTagsFn) {
    searchTagsFn = () => observableOf([])
  }
  const selection = React.useRef<Selection | null>(window.getSelection())
  const inputElementRef = React.useRef<HTMLDivElement>(null)
  // EXPL: lazily built - a useRef initialiser runs on every render, so passing
  // `new Subject()` directly allocated one per render and threw all but the first away
  const change$ = React.useRef<Subject<void> | null>(null)
  if (!change$.current) {
    change$.current = new Subject<void>()
  }
  // EXPL: keep latest guard in a ref so the native listener / callbacks below
  // never close over a stale value without re-binding on every render
  const onBeforeInputRef = React.useRef(props.onBeforeInput)
  onBeforeInputRef.current = props.onBeforeInput
  // EXPL: the change pipeline below is built once on mount, so anything it reads
  // from props must go through a ref or it stays frozen at the mount-time value
  const onChangeRef = React.useRef(props.onChange)
  onChangeRef.current = props.onChange
  const searchTagsRef = React.useRef(props.searchTags)
  searchTagsRef.current = props.searchTags
  const networkRef = React.useRef(props.network)
  networkRef.current = props.network
  const [tagsSearch, tagsResponse, _tagsError, tagsLoading] = useAsyncAction(searchTagsFn)
  const [tags, setTags] = React.useState<Feed[]>([])
  const [tagsDropdownAnchor, setTagsDropdownAnchor] = React.useState<HTMLElement | null>(null)
  const [tagPopupAnchor, setTagPopupAnchor] = React.useState<HTMLElement | null>(null)

  React.useEffect(() => {
    if (!tagsLoading && tagsResponse) {
      setTags(tagsResponse)
    }
  }, [tagsLoading, tagsResponse])

  // NOTE: Observe input subtree changes.
  // If active tag element is removed, we must close the tag suggestions dropdown
  // EXPL: the observer outlives every render, so it reaches closeTagsDropdown through a
  // ref — held directly it would keep calling the first render's copy, which always saw
  // a null anchor and so could never unwrap an active tag back to text.
  const closeTagsDropdownRef = React.useRef<(() => void) | null>(null)

  React.useEffect(() => {
    const element = inputElementRef.current
    if (!element) {
      return
    }
    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        if (m.removedNodes.length > 0) {
          setTagPopupAnchor(null)
          closeTagsDropdownRef.current?.()
          return
        }
      }
    })
    observer.observe(element, { subtree: true, childList: true })

    return () => {
      observer.disconnect()
    }
  }, [])

  // NOTE: Block input at the source. `beforeinput` is cancelable, so preventDefault
  // here stops the browser from ever writing the char into the contentEditable DOM.
  // Returning early in `onChange` only skips the redux update — the DOM is already mutated.
  React.useEffect(() => {
    const input = inputElementRef.current
    if (!input) {
      return
    }
    const handler = (e: Event) => {
      const guard = onBeforeInputRef.current
      if (guard && !guard()) {
        e.preventDefault()
      }
    }
    input.addEventListener('beforeinput', handler)
    return () => {
      input.removeEventListener('beforeinput', handler)
    }
  }, [])

  useMountEffect(() => {
    const input = inputElementRef.current
    if (input) {
      input.innerHTML = sanitizeComposerHtml(props.initialHTML)
    }
  })

  React.useEffect(() => {
    const input = inputElementRef.current
    if (typeof props.html === 'string' && input) {
      // EXPL: compare against the sanitized html, otherwise the guard never matches what
      // we actually wrote and the editor is rebuilt on every run of this effect
      const clean = sanitizeComposerHtml(props.html)
      if (input.innerHTML !== clean) {
        input.innerHTML = clean
      }
    }
  }, [props.html])

  // NOTE: Tag hover is delegated to the editor rather than bound per tag element.
  // Binding per element only covered the tags present on mount and reached every
  // `[data-tag-id]` in the document, so sibling editors shared each other's popups.
  // mouseenter/mouseleave do not bubble, hence mouseover/mouseout here.
  React.useEffect(() => {
    const input = inputElementRef.current
    if (!input) {
      return
    }
    const findTag = (e: Event) => (e.target as HTMLElement)?.closest?.('[data-tag-id]') as HTMLElement | null
    const onMouseOver = (e: Event) => {
      const tag = findTag(e)
      if (tag) {
        setTagPopupAnchor(tag)
      }
    }
    const onMouseOut = (e: Event) => {
      if (findTag(e)) {
        setTagPopupAnchor(null)
      }
    }
    input.addEventListener('mouseover', onMouseOver)
    input.addEventListener('mouseout', onMouseOut)
    return () => {
      input.removeEventListener('mouseover', onMouseOver)
      input.removeEventListener('mouseout', onMouseOut)
    }
  }, [])

  /**
   * Replaces the tag element with new text node with the same text contents.
   * @param node
   * @param active
   * @returns {Text} the new created node
   */
  const removeTagFromNode = React.useCallback((node: HTMLElement): Text => {
    const text = node.textContent || ''
    const newElement = document.createTextNode(text)
    node.replaceWith(newElement)
    return newElement
  }, [])

  const saveChanges = React.useCallback(() => {
    const inputElement = inputElementRef.current
    if (inputElement) {
      // EXPL: Save selection to restore it after text is properly generated from html
      const currentRange = selection.current && selection.current.rangeCount > 0 ? selection.current.getRangeAt(0) : null
      const html = inputElement.innerHTML
      // NOTE: innerText does not work here because of how new lines are presented. Use `getTextFromHtml` instead
      const text = getTextFromHtml(html)
      onChangeRef.current(text, html)
      // Restore selection
      if (currentRange && selection.current) {
        selection.current.removeAllRanges()
        selection.current.addRange(currentRange)
      }
    }
  }, [])

  useMountEffect(() => {
    const sub = change$.current!.pipe(
      debounceTime(CHANGE_DEBOUNCE_TIME),
      tap(saveChanges),
      filter(() => Boolean(searchTagsRef.current)),
      // Allow for changes to be propagated (props.html to be updated) before altering the html for tags search
      debounceTime(TAG_DEBOUNCE_TIME),
      map(() => {
        if (!selection.current) {
          return ''
        }

        const { focusNode, focusOffset } = selection.current
        if (!focusNode || focusNode.isSameNode(inputElementRef.current)) {
          return ''
        }

        const parent = focusNode.nodeName === NODE_NAME_TEXT
          ? focusNode.parentElement as HTMLElement
          : focusNode as HTMLElement
        const focusString = focusNode.textContent?.substring(0, focusOffset)
        const tagStart = parent.dataset.tagId ? 0 : focusString?.lastIndexOf('@')
        const needle = tagStart !== undefined && tagStart !== -1
          ? focusString?.substring(tagStart + 1)
          : ''
        if (!needle || tagStart === undefined || tagStart === -1) {
          setTags([])
          // EXPL: through the ref, for the same reason as the MutationObserver above - this
          // pipeline is built once on mount, and the mount-time `closeTagsDropdown` always saw
          // a null anchor, so it stripped `data-tag-active` but left the wrapper span behind.
          closeTagsDropdownRef.current?.()
          return ''
        }

        if (parent.dataset.tagId) {
          removeTagFromNode(parent)
          return ''
        }

        if (parent.dataset.tagActive) {
          setTagsDropdownAnchor(parent)
        } else {
          const range = new Range()
          const wrapper = document.createElement('span')
          wrapper.dataset.tagActive = 'true'
          range.setStart(focusNode, tagStart)
          range.setEnd(focusNode, focusOffset)
          range.surroundContents(wrapper)
          selection.current.removeAllRanges()
          selection.current.addRange(range)
          selection.current.collapse(wrapper.firstChild, (wrapper.firstChild as any)?.length || 0)
          setTagsDropdownAnchor(wrapper)
        }
        return needle.trim()
      }),
      // EXPL: no distinctUntilChanged here. Deleting a mention and retyping the same one
      // within the debounce window collapses to a single emission whose needle matches the
      // previous search, and dedupe swallowed it, so the dropdown never came back. Repeats
      // are otherwise rare (only trailing whitespace keeps a trimmed needle unchanged).
      filter(needle => {
        const minLength = networkRef.current === BRAND_LINKEDIN ? MIN_LI_TAG_SEARCH_LENGTH : MIN_TAG_SEARCH_LENGTH
        return !!needle && needle.length >= minLength
      })
    ).subscribe(tagsSearch)
    return () => {
      sub.unsubscribe()
    }
  })

  const onKeyboardInput = React.useCallback(() => {
    change$.current?.next()
  }, [])

  const createTagNode = React.useCallback((feed: Feed) => {
    const newTagElement = document.createElement('a')
    newTagElement.contentEditable = 'false'
    // NOTE: For FB feeds handle is the feed url. For twitter, it's only the handle so we build the full url here.
    let tagUrl
    switch (props.network) {
      case BRAND_TWITTER:
        tagUrl = `https://twitter.com/${feed.handle}`
        break
      case BRAND_FACEBOOK:
        // NOTE: for FB the handle *is* the url and comes straight from the API, so its scheme
        // has to be checked before it becomes a live href
        tagUrl = sanitizeUrl(feed.handle)
        break
      case BRAND_LINKEDIN:
        tagUrl = `https://linkedin.com/company/${feed.handle}`
        break
      case BRAND_BLUESKY:
        tagUrl = `https://bsky.app/profile/${feed.handle}`
        break
      default: tagUrl = ''
    }
    newTagElement.href = tagUrl
    newTagElement.target = '_blank'
    newTagElement.rel = 'noopener'
    newTagElement.dataset.tagId = feed.id
    newTagElement.dataset.tagUrl = tagUrl
    newTagElement.dataset.tagName = feed.title || feed.name
    newTagElement.dataset.tagHandle = feed.name
    newTagElement.dataset.tagImage = sanitizeImageUrl(feed.image)
    newTagElement.className = styles.tag
    newTagElement.innerText = feed.name

    // NOTE: For Bluesky mentions show the handle instead of display name
    if ((feed as any).type === BRAND_BLUESKY) {
      newTagElement.innerText = `@${feed.handle}`
      newTagElement.dataset.tagHandle = feed.handle
    }

    return newTagElement
  }, [props.network])

  /**
   * Unwraps the pending `data-tag-active` wrapper back into plain text and closes the
   * suggestions dropdown. `restoreCaret` puts the caret at the end of that text, which
   * is wanted when the user clicked away mid-mention but not when we close on their behalf.
   */
  const dismissTagsDropdown = React.useCallback((restoreCaret: boolean) => {
    if (tagsDropdownAnchor?.dataset.tagActive) {
      // EXPL: textContent, not innerText - innerText collapses whitespace (so a needle typed
      // with a trailing space came back changed) and is undefined outside a rendered document
      const textNode = document.createTextNode(tagsDropdownAnchor.textContent || '')
      tagsDropdownAnchor.replaceWith(textNode)
      const currentSelection = restoreCaret ? window.getSelection() : null
      if (currentSelection) {
        const range = document.createRange()
        range.setStart(textNode, textNode.length)
        range.collapse(true)
        currentSelection.removeAllRanges()
        currentSelection.addRange(range)
      }
    }
    setTagsDropdownAnchor(null)
    inputElementRef.current?.querySelector('[data-tag-active]')?.removeAttribute('data-tag-active')
  }, [tagsDropdownAnchor])

  const closeTagsDropdown = React.useCallback(() => dismissTagsDropdown(false), [dismissTagsDropdown])
  closeTagsDropdownRef.current = closeTagsDropdown

  const onTagsPopupClickAway = React.useCallback(() => dismissTagsDropdown(true), [dismissTagsDropdown])

  const onTagSelected = React.useCallback((feed: Feed) => {
    const isTaggingAvailable = dispatch(checkFeatureAvailability(FEATURE_POST_TAGGING))
    if (!isTaggingAvailable) {
      closeTagsDropdown()
      return
    }
    // EXPL: scoped to this editor. The composer keeps one TextEditor per network mounted, so a
    // document-wide lookup could resolve a sibling editor's pending mention and insert the tag
    // into the wrong network's post.
    const activeTagElement = inputElementRef.current?.querySelector('[data-tag-active]') as HTMLElement | null

    if (activeTagElement) {
      const newTagElement = createTagNode(feed)
      activeTagElement.replaceWith(newTagElement)
      newTagElement.insertAdjacentHTML('afterend', '&nbsp;')
      if (newTagElement.nextSibling) {
        const range = new Range()
        range.setStartAfter(newTagElement.nextSibling)
        range.setEndAfter(newTagElement.nextSibling)
        selection.current?.removeAllRanges()
        selection.current?.addRange(range)
      }
      saveChanges()
    }
    closeTagsDropdown()
  }, [createTagNode, saveChanges, closeTagsDropdown, dispatch])

  const onPaste = React.useCallback((event: React.ClipboardEvent) => {
    event.preventDefault()
    const guard = onBeforeInputRef.current
    if (guard && !guard()) {
      return
    }
    // Paste text only, without formatting
    const text = event.clipboardData.getData('text/plain').replace(/\r/g, '')
    if (inputElementRef.current && selection.current && selection.current.rangeCount > 0) {
      const range = selection.current.getRangeAt(0)
      const node = document.createElement('span')
      node.dataset.external = 'true'
      node.textContent = text.replace(/\n+$/, '') // remove trailing newline
      node.style.whiteSpace = 'pre-wrap'
      range.deleteContents()
      range.insertNode(node)
      // Remove extra new line if it exists
      if (node.nextElementSibling?.nodeName === 'BR') {
        node.nextElementSibling.remove()
      }
      range.collapse()
      change$.current?.next()
    }
  }, [])

  function setCursorAtEnd(node: Text | Element, offset: number | null = null): void {
    const range = document.createRange()
    const selection = window.getSelection()

    if (node instanceof Text) {
      range.setStart(node, offset !== null ? offset : node.length)
    } else if (node.firstChild instanceof Text) {
      range.setStart(node.firstChild, offset !== null ? offset : node.firstChild.length)
    } else {
      return // No valid text node to set the cursor in
    }

    range.collapse(true)
    selection?.removeAllRanges()
    selection?.addRange(range)
  }

  const insertEmoji = React.useCallback((emoji: any) => {
    const guard = onBeforeInputRef.current
    if (guard && !guard()) {
      return
    }
    if (inputElementRef.current && selection.current) {
      const focusNode = selection.current.focusNode as Text
      const focusWithinEditor = inputElementRef.current as any === focusNode || inputElementRef.current.contains(focusNode)
      if (focusWithinEditor) {
        const node = selection.current.focusNode
        const offset = selection.current.focusOffset

        // Cursor is inside an empty <div><br/></div>
        if (node?.nodeName === 'DIV' && node.firstChild?.nodeName === 'BR') {
          const newTextNode = document.createTextNode(emoji.native)
          node.replaceChild(newTextNode, node.firstChild)
          setCursorAtEnd(newTextNode)
        } else if (node?.nodeType === Node.TEXT_NODE) {
          // Cursor is inside a Text node
          (node as any).insertData(offset, emoji.native)
          setCursorAtEnd(node as any, offset + emoji.native.length)
        } else if (node?.nodeType === Node.ELEMENT_NODE) {
          // Cursor is inside an Element but not a text node
          const newTextNode = document.createTextNode(emoji.native)
          node.appendChild(newTextNode)
          setCursorAtEnd(newTextNode)
        }
      } else {
        const newTextNode = document.createTextNode(emoji.native)
        inputElementRef.current.appendChild(newTextNode)
      }
      change$.current?.next()
    }
  }, [])

  /**
   * Returns the selected atomic node (a mention or a caption placeholder) when the whole of it
   * is selected. Those are `contenteditable="false"`, so the browser will not remove them on
   * its own - every keystroke that replaces the selection has to do it programmatically.
   */
  const getSelectedAtomicNode = React.useCallback((): HTMLElement | null => {
    const currentSelection = selection.current
    if (!currentSelection || currentSelection.isCollapsed || currentSelection.focusNode !== currentSelection.anchorNode) {
      return null
    }
    const element = currentSelection.focusNode?.parentElement
    const data = element?.dataset
    return data?.tagId || data?.code ? element as HTMLElement : null
  }, [])

  const onKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeTagsDropdown()
    }
    // EXPL: only a keystroke that replaces the selection may remove the node: Backspace/Delete,
    // Enter, or typing a printable character over it. Anything else (copy, arrows, tab, other
    // shortcuts) must leave a selected tag alone. Cut is handled in `onCut`, where the clipboard
    // still gets the text.
    const isPrintable = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey
    const replacesSelection = e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Enter' || isPrintable
    if (!replacesSelection) {
      return
    }

    // EXPL: Bugfix - can't delete a tag when selected.
    const tagElement = getSelectedAtomicNode()
    if (!tagElement) {
      return
    }
    if (isPrintable) {
      // EXPL: preventDefault below also swallows the character, so the tag is swapped for it
      // by hand rather than only removed
      const textNode = document.createTextNode(e.key)
      tagElement.replaceWith(textNode)
      setCursorAtEnd(textNode)
    } else {
      tagElement.remove()
    }
    e.preventDefault()
    e.stopPropagation()
    // EXPL: preventDefault suppresses the browser's `input` event, so the removal
    // has to be propagated by hand or the deleted tag survives in the saved html
    change$.current?.next()
  }, [closeTagsDropdown, getSelectedAtomicNode])

  const onCut = React.useCallback((e: React.ClipboardEvent) => {
    const tagElement = getSelectedAtomicNode()
    if (!tagElement) {
      return
    }
    // EXPL: the browser cannot cut a contenteditable="false" node, so fill the clipboard and
    // remove the node here instead of letting the default run
    e.clipboardData.setData('text/plain', tagElement.textContent || '')
    tagElement.remove()
    e.preventDefault()
    change$.current?.next()
  }, [getSelectedAtomicNode])

  const preventDefaultEvent = React.useCallback((e: any) => {
    e.preventDefault()
  }, [])

  const addPlaceholder = React.useCallback((placeholder: CaptionPlaceholder) => {
    if (selection.current) {
      const focusNode = selection.current.focusNode
      const focusWithinEditor = Boolean(focusNode?.parentElement?.closest('[data-composer-editor]'))
        && inputElementRef.current?.contains(focusNode)
      const placeholderElement = document.createElement('span')
      placeholderElement.textContent = `[[${placeholder}]]`
      placeholderElement.classList.add(styles.placeholder)
      placeholderElement.contentEditable = 'false'
      placeholderElement.dataset.code = placeholder

      if (focusWithinEditor && selection.current?.rangeCount > 0) {
        const range = selection.current.getRangeAt(0)
        range.deleteContents()
        range.insertNode(placeholderElement)
        selection.current.collapseToEnd()
      } else {
        inputElementRef.current?.appendChild(placeholderElement)
      }
      change$.current?.next()
    }
  }, [])

  const disabledClassName = props.disabled ? props.disabledClassName : ''
  const withTextActions = props.templateStrings && props.templateStrings.length > 0

  return (
    <div className={`${styles.container} ${props.containerClassName || ''}`} data-testid={props.id}>
      {props.withGenerateText && <AIChatPanel className={styles['generate-btn']} />}
      <div
        ref={inputElementRef}
        tabIndex={props.tabIndex}
        contentEditable={!props.disabled}
        data-placeholder={props.disabled ? '' : props.placeholder || 'Add your text or link here'}
        data-composer-editor
        className={`${styles.content} ${disabledClassName} ${props.inputClassName || ''} ${props.hidden ? styles.hidden : ''}`}
        onInput={onKeyboardInput}
        onKeyDown={onKeyDown}
        onCut={onCut}
        onPaste={onPaste}
        onDragOver={preventDefaultEvent}
        onDrop={preventDefaultEvent}
      >
      </div>
      {!props.disabled && (
        <div className={`${styles.actions} ${props.footerClassName || ''} ${withTextActions ? styles['w-text-actions'] : ''}`}>
          {props.footerElements}
          {props.withEmojis && (
            <EmojiPicker
              className={styles['btn-emoji']}
              onSelect={insertEmoji}
            />
          )}
        </div>
      )}
      {withTextActions && (
        <div className={styles['text-actions']}>
          {props.templateStrings && props.templateStrings.map(placeholder => (
            <PlaceholderActionButton key={placeholder} placeholder={placeholder} onClick={addPlaceholder} />
          ))}
          <div className={styles.label}>
            <FormattedMessage id="composer.caption-placeholder.hint" />
          </div>
        </div>
      )}
      <ClickAwayListener onClickAway={onTagsPopupClickAway}>
        <Popper
          open={Boolean(tagsDropdownAnchor)}
          anchorEl={tagsDropdownAnchor}
          placement="bottom-start"
          className={styles.popper}
        // MUI Not supported: modifiers={{ preventOverflow: { priority: ['top', 'bottom'] } }}
        >
          <Paper className={styles['popper-content']} data-testid="tag-suggestions">
            <FeedsList
              feeds={tags}
              loading={tagsLoading}
              emptyResultsMessage="No results found."
              onFeedClick={onTagSelected}
            />
          </Paper>
        </Popper>
      </ClickAwayListener>
      <TagPopup anchor={tagPopupAnchor} />
    </div>
  )
}

const PlaceholderActionButton = React.memo(function PlaceholderActionButton(
  props: { placeholder: CaptionPlaceholder, onClick: (value: CaptionPlaceholder) => void }
) {
  const click = (e: any) => {
    props.onClick(props.placeholder)
    // EXPL: prevent subsequent clicks triggered by "space" or "enter" key press
    ; (e.target as HTMLButtonElement).blur()
  }

  return (
    <Button className={styles.btn} onClick={click}>
      <FormattedMessage id={`composer.caption-codes.${props.placeholder}`} />
    </Button>
  )
})

export default TextEditor
